import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import logger from '../utils/logger';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Plus, Pencil, Trash2, Eye, EyeOff, Video, ChevronUp, ChevronDown } from 'lucide-react';
import SimpleTooltip from './SimpleTooltip';
import { toast } from 'sonner';
import { VideoForm } from './walkthrough/VideoForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminWalkthroughVideos({ adminToken }) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editVideo, setEditVideo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const token = adminToken || localStorage.getItem('auth_token');
  const headers = { 'Authorization': `Bearer ${token}` };

  const fetchVideos = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/walkthrough-videos`, { headers, cache: 'no-store' });
      const d = await res.json();
      setVideos(d.videos || []);
    } catch (e) { logger.error('Failed to fetch videos:', e); }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchVideos(); }, [fetchVideos]);

  const handleSave = async (formData, videoId) => {
    setSaving(true);
    try {
      const url = videoId ? `${BACKEND_URL}/api/admin/walkthrough-videos/${videoId}` : `${BACKEND_URL}/api/admin/walkthrough-videos`;
      const res = await fetch(url, { method: videoId ? 'PUT' : 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      toast.success(videoId ? 'Video updated' : 'Video created');
      setEditVideo(null); fetchVideos();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const handleDelete = async (videoId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/walkthrough-videos/${videoId}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error('Failed');
      toast.success('Video deleted'); setDeleteConfirm(null); fetchVideos();
    } catch (err) { logger.error(err); toast.error('Failed to delete'); }
  };

  const handleReorder = async (videoId, direction) => {
    const idx = videos.findIndex(v => v.video_id === videoId);
    if ((direction === -1 && idx === 0) || (direction === 1 && idx === videos.length - 1)) return;
    const newOrder = [...videos];
    [newOrder[idx], newOrder[idx + direction]] = [newOrder[idx + direction], newOrder[idx]];
    setVideos(newOrder);
    try {
      await fetch(`${BACKEND_URL}/api/admin/walkthrough-videos/reorder`, { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify({ order: newOrder.map(v => v.video_id) }) });
    } catch (e) { logger.error('Failed to reorder videos:', e); }
  };

  const togglePublish = async (video) => {
    const formData = new FormData();
    formData.append('is_published', !video.is_published);
    await fetch(`${BACKEND_URL}/api/admin/walkthrough-videos/${video.video_id}`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
    fetchVideos();
    toast.success(video.is_published ? 'Video unpublished' : 'Video published');
  };

  if (loading) return <div className="text-center py-8 text-gray-400">Loading videos...</div>;

  return (
    <div className="space-y-4" data-testid="admin-walkthrough-videos">
      <div className="flex items-center justify-between">
        <div><h3 className="text-lg font-bold text-white">Walkthrough Videos</h3><p className="text-sm text-gray-400">{videos.length} videos total</p></div>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setEditVideo({})} data-testid="add-video-btn"><Plus className="w-4 h-4 mr-1.5" /> Add Video</Button>
      </div>
      <div className="space-y-2">
        {videos.map((video, idx) => (
          <Card key={video.video_id} className="bg-gray-800 border-gray-700" data-testid={`admin-video-${video.video_id}`}>
            <CardContent className="p-3 flex items-center gap-3">
              <div className="flex flex-col gap-0.5">
                <SimpleTooltip content="Move up">
                  <button onClick={() => handleReorder(video.video_id, -1)} disabled={idx === 0} aria-label="Move video up" className="text-gray-500 hover:text-white disabled:opacity-20 transition" data-testid={`reorder-up-${video.video_id}`}><ChevronUp className="w-4 h-4" /></button>
                </SimpleTooltip>
                <SimpleTooltip content="Move down">
                  <button onClick={() => handleReorder(video.video_id, 1)} disabled={idx === videos.length - 1} aria-label="Move video down" className="text-gray-500 hover:text-white disabled:opacity-20 transition" data-testid={`reorder-down-${video.video_id}`}><ChevronDown className="w-4 h-4" /></button>
                </SimpleTooltip>
              </div>
              <div className="w-3 h-3 rounded-full shrink-0" style={{ background: video.color }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm text-white truncate">{video.title}</span>
                  {!video.is_published && <Badge className="bg-yellow-500/20 text-yellow-400 text-[10px]">Draft</Badge>}
                  {video.video_url && <Badge className="bg-green-500/20 text-green-400 text-[10px]"><Video className="w-2.5 h-2.5 mr-0.5" /> Has Video</Badge>}
                </div>
                <p className="text-xs text-gray-400 truncate">{video.subtitle} &middot; {video.duration_label} &middot; {video.sections?.length || 0} sections</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <SimpleTooltip content={video.is_published ? 'Unpublish' : 'Publish'}>
                  <Button variant="ghost" size="icon" aria-label={video.is_published ? 'Unpublish video' : 'Publish video'} className="h-8 w-8 text-gray-400 hover:text-white" onClick={() => togglePublish(video)}>{video.is_published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</Button>
                </SimpleTooltip>
                <SimpleTooltip content="Edit video">
                  <Button variant="ghost" size="icon" aria-label="Edit video" className="h-8 w-8 text-gray-400 hover:text-white" onClick={() => setEditVideo(video)} data-testid={`edit-video-${video.video_id}`}><Pencil className="w-4 h-4" /></Button>
                </SimpleTooltip>
                <SimpleTooltip content="Delete video">
                  <Button variant="ghost" size="icon" aria-label="Delete video" className="h-8 w-8 text-red-400 hover:text-red-300" onClick={() => setDeleteConfirm(video.video_id)} data-testid={`delete-video-${video.video_id}`}><Trash2 className="w-4 h-4" /></Button>
                </SimpleTooltip>
              </div>
            </CardContent>
          </Card>
        ))}
        {videos.length === 0 && <div className="text-center py-8 text-gray-500">No walkthrough videos yet. Click "Add Video" to create one.</div>}
      </div>
      <Dialog open={editVideo !== null} onOpenChange={(open) => !open && setEditVideo(null)}>
        <DialogContent className="max-w-2xl bg-gray-900 border-gray-700 text-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editVideo?.video_id ? 'Edit Video' : 'Add New Video'}</DialogTitle>
            <DialogDescription className="text-gray-400">Configure walkthrough video metadata, sections, and visibility.</DialogDescription>
          </DialogHeader>
          {editVideo !== null && <VideoForm video={editVideo} onSave={handleSave} onCancel={() => setEditVideo(null)} saving={saving} />}
        </DialogContent>
      </Dialog>
      <Dialog open={deleteConfirm !== null} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Video?</DialogTitle>
            <DialogDescription className="text-gray-400">This will permanently remove this walkthrough video and its uploaded files.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="border-gray-600 text-gray-300" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={() => handleDelete(deleteConfirm)} data-testid="confirm-delete-video">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
